"use client"

// Collapsible left sidebar with conversation history (ChatGPT-style).
//
// Lists the user's saved conversations most-recent-first, grouped by recency,
// with the active one highlighted and a per-row delete (behind a confirm). The
// header's panel toggle controls `open`; the sign-out control lives at the
// bottom (a user-menu pattern). History data comes from `useSessions` via the
// parent — this component is presentational.

import { useState } from "react"
import {
  SquarePen,
  LogOut,
  Trash2,
  Pencil,
  SlidersHorizontal,
  Loader2,
  PanelRightOpen,
  PanelLeftOpen,
  X,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { copy } from "@/lib/copy"
import logoAskme from "@/assets/brand/ASKme-LOGO2x.png"
import logoAskmeWhite from "@/assets/brand/ASKme-LOGO2x-white.png"
import { useIsDark } from "@/components/theme/theme"
import { LOCAL_AGENT } from "@/lib/localDev"
import { useAuth } from "@/hooks/useAuth"
import { useIsMobile } from "@/hooks/UseMobile"
import { PreferencesSheet } from "./PreferencesSheet"
import { Input } from "@/components/ui/input"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import type { SessionSummary } from "./types"

interface AppSidebarProps {
  /** Whether the sidebar is expanded. */
  open: boolean
  /** Toggle the sidebar open/closed. */
  onToggle: () => void
  /** The user's saved conversations, most-recently-updated first. */
  sessions: SessionSummary[]
  /** Id of the conversation currently shown in the thread. */
  activeId: string
  /** Id of the conversation with a turn currently streaming, or null. */
  streamingId?: string | null
  /** Start a new conversation. */
  onNewChat: () => void
  /** Whether starting a new conversation is currently allowed. */
  canStartNewChat: boolean
  /** Open a saved conversation by id. */
  onSelectSession: (id: string) => void
  /** Delete a saved conversation by id. */
  onDeleteSession: (id: string) => void
  /** Rename a saved conversation to a user-chosen title. */
  onRenameSession: (id: string, title: string) => void
  /** Close the sidebar (used by the mobile full-screen close control). */
  onClose?: () => void
  /** Whether the preferences feature (its REST route) is available. */
  preferencesEnabled?: boolean
}

// A recency bucket and the sessions that fall into it, in display order.
interface SessionGroup {
  label: string
  sessions: SessionSummary[]
}

/**
 * Bucket a session into a recency group by its `updatedAt`.
 *
 * @param iso - the session's ISO-8601 `updatedAt`.
 * @returns the localized group label (today / yesterday / older).
 */
function recencyLabel(iso: string): string {
  const startOfToday = new Date()
  startOfToday.setHours(0, 0, 0, 0)
  const dayMs = 24 * 60 * 60 * 1000
  const t = new Date(iso).getTime()
  if (t >= startOfToday.getTime()) return copy.sidebar.groupToday
  if (t >= startOfToday.getTime() - dayMs) return copy.sidebar.groupYesterday
  return copy.sidebar.groupOlder
}

/**
 * Partition already-sorted summaries into ordered recency groups. Input is
 * assumed most-recent-first, so groups come out in Today → Yesterday → Older
 * order without re-sorting.
 *
 * @param sessions - summaries ordered most-recently-updated first.
 * @returns non-empty groups in display order.
 */
function groupByRecency(sessions: SessionSummary[]): SessionGroup[] {
  const groups: SessionGroup[] = []
  for (const session of sessions) {
    const label = recencyLabel(session.updatedAt)
    const last = groups[groups.length - 1]
    if (last && last.label === label) {
      last.sessions.push(session)
    } else {
      groups.push({ label, sessions: [session] })
    }
  }
  return groups
}

/**
 * Full-height collapsible sidebar. Collapses to zero width (content clipped)
 * when closed so the conversation takes the full width.
 *
 * @param open - expanded state.
 * @param sessions - saved conversation summaries (most-recent-first).
 * @param activeId - id of the active conversation.
 * @param onNewChat - new-conversation handler.
 * @param canStartNewChat - whether new chat is enabled.
 * @param onSelectSession - handler to open a saved conversation.
 * @param onDeleteSession - handler to delete a saved conversation.
 * @param onRenameSession - handler to rename a saved conversation.
 */
export function AppSidebar({
  open,
  onToggle,
  sessions,
  activeId,
  streamingId,
  onNewChat,
  canStartNewChat,
  onSelectSession,
  onDeleteSession,
  onRenameSession,
  onClose,
  preferencesEnabled = false,
}: AppSidebarProps) {
  const { isAuthenticated, user, signOut } = useAuth()
  const isMobile = useIsMobile()
  const isDark = useIsDark()
  // On mobile the sidebar is full-screen, so navigating (new chat / opening a
  // conversation) should also dismiss it to reveal the thread underneath.
  const handleNewChat = () => {
    onNewChat()
    if (isMobile) onClose?.()
  }
  const handleSelectSession = (id: string) => {
    onSelectSession(id)
    if (isMobile) onClose?.()
  }
  // Id of the session pending a delete confirmation, or null when none. A single
  // dialog is reused for every row rather than one dialog per list item.
  const [pendingDelete, setPendingDelete] = useState<string | null>(null)

  // Whether the preferences panel is open.
  const [prefsOpen, setPrefsOpen] = useState(false)

  // Inline-rename state: the row being edited and its working draft title.
  const [editingId, setEditingId] = useState<string | null>(null)
  const [draft, setDraft] = useState("")

  // User email for the account row (from the Cognito ID token).
  const profile = (user?.profile ?? null) as { email?: string } | null
  const email = profile?.email ?? ""

  const groups = groupByRecency(sessions)

  const FOCUS_RING = "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"

  /** Confirm the pending deletion and close the dialog. */
  const confirmDelete = () => {
    if (pendingDelete) onDeleteSession(pendingDelete)
    setPendingDelete(null)
  }

  /** Enter inline-rename mode for a session, seeding the draft with its title. */
  const startRename = (session: SessionSummary) => {
    setEditingId(session.id)
    setDraft(session.title)
  }

  /** Commit the draft title (ignored if empty) and leave rename mode. */
  const commitRename = () => {
    if (editingId && draft.trim()) onRenameSession(editingId, draft)
    setEditingId(null)
    setDraft("")
  }

  /** Abandon the rename without saving. */
  const cancelRename = () => {
    setEditingId(null)
    setDraft("")
  }

  return (
    <aside
      className={cn(
        "h-full shrink-0 overflow-hidden bg-sidebar text-sidebar-foreground transition-[width] duration-200 ease-out",
        open ? "w-71.25" : "w-45"
      )}
    >
      <div className="flex h-full min-w-45 flex-col">
        {/* Logo + toggle */}
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <img
            src={isDark ? logoAskmeWhite : logoAskme}
            alt={copy.app.title}
            className={cn("w-auto transition-all", open ? "h-10" : "h-7")}
          />
          <button
            type="button"
            onClick={onToggle}
            aria-label={copy.sidebar.toggle}
            aria-pressed={open}
            title={copy.sidebar.toggle}
            className={`inline-flex size-9 items-center justify-center rounded-md text-foreground transition-colors hover:bg-accent hover:text-foreground ${FOCUS_RING}`}
          >
            {open ? (
              <PanelRightOpen className="size-4.5" aria-hidden="true" />
            ) : (
              <PanelLeftOpen className="size-4.5" aria-hidden="true" />
            )}
          </button>
        </div>

        {/* Full-screen on mobile needs its own dismiss control — the header
            toggle lives in the chat area, which is covered while open. */}
        <div className="flex items-center justify-between px-4 py-2 md:hidden">
          <button
            type="button"
            onClick={onClose}
            aria-label={copy.sidebar.close}
            title={copy.sidebar.close}
            className={`inline-flex size-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground ${FOCUS_RING}`}
          >
            <X className="size-5" aria-hidden="true" />
          </button>
        </div>

        <div className="px-4 py-2">
          <button
            type="button"
            onClick={handleNewChat}
            disabled={!canStartNewChat}
            className={`group flex w-full items-center justify-center gap-2 rounded-lg border-2 border-transparent bg-header px-4 py-2.5 text-sm font-medium text-header-foreground transition-colors hover:bg-header/80 dark:bg-primary dark:text-primary-foreground dark:hover:bg-newchat-hover dark:focus-visible:border-background dark:focus-visible:outline-none dark:disabled:bg-newchat-disabled-bg dark:disabled:text-newchat-disabled-fg dark:disabled:opacity-100 ${FOCUS_RING} disabled:pointer-events-none disabled:opacity-40`}
          >
            <SquarePen className="size-4 shrink-0 text-header-foreground dark:text-primary-foreground dark:group-disabled:text-newchat-disabled-fg" aria-hidden="true" />
            <span>{copy.header.newChat}</span>
          </button>
        </div>

        <nav aria-label={copy.sidebar.history} className="mt-4 flex-1 overflow-y-auto px-4">
          <div className="mb-2 flex items-center gap-2">
            <span className="size-2 bg-brand-primary" aria-hidden="true" />
            <span className="font-body text-xs font-semibold uppercase tracking-wide text-brand-primary">Recientes</span>
          </div>
          {sessions.length === 0 ? (
            <p className="px-1 py-2 text-xs text-muted-foreground">{copy.sidebar.empty}</p>
          ) : (
            groups.map(group => (
              <div key={group.label} className="mb-3">
                <ul className="space-y-0.5">
                  {group.sessions.map(session => {
                    const isActive = session.id === activeId
                    const isEditing = session.id === editingId
                    const isStreaming = session.id === streamingId
                    return (
                      <li key={session.id} className="group/item relative">
                        {isEditing ? (
                          <Input
                            autoFocus
                            value={draft}
                            onChange={e => setDraft(e.target.value)}
                            onBlur={commitRename}
                            onKeyDown={e => {
                              if (e.key === "Enter") {
                                e.preventDefault()
                                commitRename()
                              } else if (e.key === "Escape") {
                                e.preventDefault()
                                cancelRename()
                              }
                            }}
                            aria-label={copy.sidebar.renameAria}
                            className="h-9 text-sm border-sidebar-input-border outline-ring focus-visible:ring-ring [&::selection]:bg-sidebar-selection [&::selection]:text-foreground"
                          />
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={() => handleSelectSession(session.id)}
                              aria-current={isActive ? "true" : undefined}
                              aria-label={`${copy.sidebar.openAria}: ${session.title}`}
                              className={cn(
                                `flex w-full items-center rounded-lg py-2 pl-3 pr-16 text-left font-body text-sm font-semibold transition-colors ${FOCUS_RING}`,
                                isActive
                                  ? "bg-sidebar-active text-foreground"
                                  : "text-foreground hover:bg-sidebar-item-hover"
                              )}
                            >
                              {isStreaming && (
                                <Loader2
                                  className="mr-2 size-4 shrink-0 animate-spin text-primary"
                                  aria-label={copy.sidebar.streamingAria}
                                />
                              )}
                              <span className="min-w-0 flex-1 truncate">{session.title}</span>
                            </button>
                            <div className="absolute right-1 top-1/2 flex -translate-y-1/2 items-center opacity-0 transition-opacity focus-within:opacity-100 group-hover/item:opacity-100">
                              <button
                                type="button"
                                onClick={() => startRename(session)}
                                aria-label={`${copy.sidebar.renameAria}: ${session.title}`}
                                className={`rounded-sm p-1.5 text-sidebar-icon ${FOCUS_RING}`}
                              >
                                <Pencil className="size-3.5" aria-hidden="true" />
                              </button>
                              <button
                                type="button"
                                onClick={() => setPendingDelete(session.id)}
                                aria-label={`${copy.sidebar.deleteAria}: ${session.title}`}
                                className={`rounded-sm p-1.5 text-sidebar-icon ${FOCUS_RING}`}
                              >
                                <Trash2 className="size-3.5" aria-hidden="true" />
                              </button>
                            </div>
                          </>
                        )}
                      </li>
                    )
                  })}
                </ul>
              </div>
            ))
          )}
        </nav>

        {/* User options — always visible */}
        <div className="mt-auto px-4 pb-4">
          {/* Section header */}
          <div className="mb-3 flex items-center gap-2">
            <span className="size-2 bg-brand-primary" aria-hidden="true" />
            <span className="font-body text-xs font-semibold uppercase tracking-wide text-brand-primary">Opciones de usuario</span>
          </div>

          {/* Divider */}
          <div className="mb-3 h-px bg-brand-primary" />

          {/* User email */}
          <p className="mb-3 truncate font-body text-sm text-sidebar-meta">
            {email || "Cuenta"}
          </p>

          {/* Preferences */}
          {preferencesEnabled && (
            <button
              type="button"
              onClick={() => setPrefsOpen(true)}
              className={`flex w-full items-center gap-2 py-2 text-sm font-medium text-foreground transition-colors hover:text-foreground/80 ${FOCUS_RING}`}
            >
              <SlidersHorizontal className="size-4 shrink-0 text-foreground" aria-hidden="true" />
              <span>{copy.preferences.menuItem}</span>
            </button>
          )}

          {/* Sign out */}
          {(isAuthenticated && !LOCAL_AGENT) && (
            <button
              type="button"
              onClick={() => signOut()}
              className={`flex w-full items-center gap-2 py-2 text-sm font-medium text-foreground transition-colors hover:text-foreground/80 ${FOCUS_RING}`}
            >
              <LogOut className="size-4 shrink-0 text-foreground" aria-hidden="true" />
              <span>{copy.header.logout}</span>
            </button>
          )}
        </div>
      </div>

      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={open => {
          if (!open) setPendingDelete(null)
        }}
      >
        {" "}
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{copy.sidebar.deleteConfirmTitle}</AlertDialogTitle>
            <AlertDialogDescription>{copy.sidebar.deleteConfirmDescription}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{copy.sidebar.deleteCancel}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>
              {copy.sidebar.deleteConfirm}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {preferencesEnabled && <PreferencesSheet open={prefsOpen} onOpenChange={setPrefsOpen} />}
    </aside>
  )
}
